'use client'

import type { Address } from 'viem'

import { Avatar, type AvatarProps } from '@repo/ui'
import { getTokenIconUrl } from '@repo/utils'
import { type ComponentProps, useCallback, useState } from 'react'

import type { ChainId } from '../web3.types'

import { ChainIcon } from './ChainIcon'

export type TokenIconProps = ComponentProps<typeof TokenIcon>

export function TokenIcon({
  address,
  symbol,
  chainId,
  ...props
}: {
  address?: string | Address
  symbol?: string | null
  chainId?: ChainId | number
} & AvatarProps) {
  const tokenIconUrl = symbol ? getTokenIconUrl(symbol) : null

  // Track which specific token has failed (by its unique key), so the state
  // resets when the same component instance re-renders for a different token.
  const currentTokenKey = `${symbol || ''}-${address || ''}`
  const [failedTokenKey, setFailedTokenKey] = useState<string | null>(null)

  const hasTokenIconFailed = failedTokenKey === currentTokenKey
  const src = tokenIconUrl && !hasTokenIconFailed ? tokenIconUrl : null

  const fallbackText = symbol
    ? symbol.slice(0, 1).toUpperCase()
    : address
      ? address.slice(2, 3).toUpperCase()
      : '0x'

  // The <img> ships in the SSR HTML, so a failing icon (e.g. an unlisted symbol
  // whose CDN URL 404s) can finish loading before hydration attaches `onError` —
  // and React does not replay a missed error event. On mount, read the decoded
  // state directly (`complete` with zero natural size ⇒ broken) so we reveal the
  // letter fallback in that race too, not only for post-hydration failures.
  const detectBrokenIcon = useCallback(
    (node: HTMLImageElement | null) => {
      if (node?.complete && node.naturalWidth === 0) {
        setFailedTokenKey(currentTokenKey)
      }
    },
    [currentTokenKey]
  )

  return (
    <div className="relative inline-flex">
      <Avatar {...props}>
        {/* Letter fallback sits behind the image: visible while the (opaque)
            logo loads and if it fails. Always rendered so we don't depend on the
            image's `onLoad` firing — a cached image can fire it before hydration
            attaches the handler, which would otherwise strand the fallback. */}
        <span
          aria-hidden
          className="bg-muted text-4xs flex size-full items-center justify-center rounded-full"
        >
          {fallbackText}
        </span>
        {/* Native <img> (not Radix `AvatarImage`) so the icon ships in the SSR
            HTML and the browser fetches it during parse, instead of waiting for a
            client-side `Image()` preload after hydration — the icons no longer lag
            behind the rest of the now-SSR'd shell. `key` remounts it when the token
            changes so error state can't leak across tokens. */}
        {src && (
          <img
            key={currentTokenKey}
            ref={detectBrokenIcon}
            src={src}
            alt={`${symbol} icon`}
            decoding="async"
            className="absolute inset-0 aspect-square size-full object-cover"
            onError={() => setFailedTokenKey(currentTokenKey)}
          />
        )}
      </Avatar>
      {chainId != null && (
        <ChainIcon
          chainId={chainId}
          className="ring-background absolute -right-0.5 -bottom-0.5 size-[40%] min-h-2.5 min-w-2.5 rounded-full ring-1"
        />
      )}
    </div>
  )
}
